// Каркас: боковое меню (компьютер), шапка и нижнее меню (телефон), панель чата, палитра команд.

import { Toasts } from '@autoaff/ui';
import { Link, Outlet, useRouterState } from '@tanstack/react-router';
import { Clapperboard, Ellipsis, Inbox, LayoutDashboard, MessageCircle, Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { ChatPanel } from '../chat/ChatPanel';
import { chatStore, useChat } from '../chat/store';
import { useAgentStatus } from '../streams';
import { AgentIndicator, agentState } from './AgentIndicator';
import { AlertBar } from './AlertBar';
import { Mark } from './Mark';
import { NetBar } from './NetBar';
import { MENU, SECTIONS } from './nav';
import { Palette } from './Palette';
import { useWaitingSync, useWaitingTotal } from './waiting';

function isActive(path: string, to: string): boolean {
  return to === '/' ? path === '/' : path === to || path.startsWith(`${to}/`);
}

/** Разделы с собственной кнопкой в нижнем меню телефона; всё остальное открывается из «Ещё» и подсвечивает его. */
const IN_TABBAR = ['/', '/decisions', '/creatives'];

export function Shell() {
  const path = useRouterState({ select: (s) => s.location.pathname });
  const [palette, setPalette] = useState(false);
  const chat = useChat();
  const { status, online } = useAgentStatus();
  // «Ждёт вас» — одно число для меню, нижней навигации, индикатора агента и «Решений» (shell/waiting.ts)
  useWaitingSync();
  const pending = useWaitingTotal() ?? 0;
  const st = agentState(status, online, pending);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPalette((p) => !p);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Контекст для чата — раздел, в котором находится владелец
  useEffect(() => {
    const s = SECTIONS.find((x) => isActive(path, x.to));
    chatStore.setContext(s ? { kind: 'page', label: s.label } : null);
  }, [path]);

  return (
    <div className="app">
      <aside className="side" aria-label="Боковое меню">
        <Link to="/" className="brand" aria-label="AutoAFF — сводка">
          <span className="logo">
            <Mark />
          </span>
          <b>AutoAFF</b>
        </Link>
        <button type="button" className="search-btn" onClick={() => setPalette(true)}>
          <Search />
          <span>Найти или спросить</span>
          <kbd>Ctrl K</kbd>
        </button>
        <nav className="nav" aria-label="Разделы">
          {MENU.map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={s.to} style={{ display: 'contents' }}>
                {(i === 3 || i === 8) && <div className="nav-sep" />}
                <Link
                  to={s.to}
                  aria-current={
                    isActive(path, s.to) || SECTIONS.some((x) => x.under === s.to && isActive(path, x.to))
                      ? 'page'
                      : undefined
                  }
                >
                  <Icon />
                  <span>{s.label}</span>
                  {s.to === '/decisions' && pending > 0 && <span className="badge">{pending}</span>}
                </Link>
              </div>
            );
          })}
        </nav>
        <AgentIndicator />
      </aside>

      <header className="mbar">
        <Link to="/" className="brand" aria-label="AutoAFF — сводка">
          <span className="logo">
            <Mark />
          </span>
          <b>AutoAFF</b>
        </Link>
        <button type="button" className="pill" onClick={() => chatStore.open()}>
          <span className={`status-dot ${st.tone}`} />
          {st.title}
        </button>
        <button
          type="button"
          className="icon-btn"
          aria-label="Поиск и команды"
          onClick={() => setPalette(true)}
        >
          <Search />
        </button>
      </header>

      <main className="main" id="main">
        <NetBar />
        <AlertBar />
        <Outlet />
      </main>

      <nav className="tabbar" aria-label="Разделы">
        <Link to="/" aria-current={path === '/' && !chat.open ? 'page' : undefined}>
          <LayoutDashboard />
          <span>Сводка</span>
        </Link>
        <Link to="/decisions" aria-current={isActive(path, '/decisions') && !chat.open ? 'page' : undefined}>
          <Inbox />
          <span>Решения</span>
          {pending > 0 && <span className="badge">{pending}</span>}
        </Link>
        <button type="button" aria-current={chat.open ? 'page' : undefined} onClick={() => chatStore.open()}>
          <MessageCircle />
          <span>Чат</span>
        </button>
        <Link to="/creatives" aria-current={isActive(path, '/creatives') && !chat.open ? 'page' : undefined}>
          <Clapperboard />
          <span>Креативы</span>
        </Link>
        <Link
          to="/more"
          aria-current={!chat.open && !IN_TABBAR.some((to) => isActive(path, to)) ? 'page' : undefined}
        >
          <Ellipsis />
          <span>Ещё</span>
        </Link>
      </nav>

      <ChatPanel />
      {palette && <Palette onClose={() => setPalette(false)} />}
      <Toasts />
    </div>
  );
}
